import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { SpeedInsights } from "@vercel/speed-insights/react";
import React, { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AdBannerController from "./components/AdBannerController";
import AppLoader from "./components/AppLoader";
import DeepLinkHandler from "./components/DeepLinkHandler";
import FirstVisitModal from "./components/FirstVisitModal";
import Footer from "./components/Footer";
import GetAppRedirect from "./components/GetAppRedirect";
import ScrollToTop from "./components/ScrollToTop";
import "./css/global.css";
import { useAppAnalyticsTracking } from "./hooks/useAppAnalyticsTracking";
import { useGAClickTracking } from "./hooks/useGAClickTracking";
import { useGAPageTracking } from "./hooks/useGAPageTracking";
import "./i18n"; // Initialize i18n
import { theme } from "./theme";
import {
  applyAppPreferences,
  getStoredAppPreferences,
} from "./utils/appPreferences";
import { IS_IOS_APP } from "./utils/platform";

const loadHome = () => import("./components/Home");
const loadCricketScorer = () => import("./components/CricketScorer");
const loadJoinGame = () => import("./components/JoinGame");
const loadViewCricketScorer = () => import("./components/ViewCricketScorer");
const loadViewSavedMatch = () => import("./components/ViewSavedMatch");
const loadPrivacyPolicy = () => import("./components/PrivacyPolicy");
const loadDisclaimer = () => import("./components/Disclaimer");
const loadHowItWorks = () => import("./components/HowItWorks");
const loadAbout = () => import("./components/About");
const loadDownloadAppPage = () => import("./components/DownloadAppPage");
const loadAppPreferencesPage = () => import("./components/AppPreferencesPage");
const loadMatchHistoryPage = () => import("./components/MatchHistoryPage");
const loadTournamentManager = () => import("./components/TournamentManager");
const loadTournamentDetail = () => import("./components/TournamentDetail");
const loadTeamLibraryManager = () => import("./components/TeamLibraryManager");
const loadSiteMapPage = () => import("./components/SiteMapPage");
const loadSupportPage = () => import("./components/SupportPage");
const loadCricketScoringGuide = () =>
  import("./components/CricketScoringGuide");
const loadFaqPage = () => import("./components/FaqPage");
const loadContactPage = () => import("./components/ContactPage");
const loadTermsOfUse = () => import("./components/TermsOfUse");
const loadScorekeepingTips = () => import("./components/ScorekeepingTips");
const loadCricketRulesGuide = () => import("./components/CricketRulesGuide");
const loadCricketMatchFormats = () =>
  import("./components/CricketMatchFormats");
const loadCricketStatisticsGuide = () =>
  import("./components/CricketStatisticsGuide");
const loadCricketResources = () => import("./components/CricketResources");
const loadCricketTournamentGuide = () =>
  import("./components/CricketTournamentGuide");
const loadNotFound = () => import("./components/NotFound");
const loadLearnHub = () => import("./components/LearnHub");
const loadArticlePage = () => import("./components/ArticlePage");
const loadCricketGlossary = () => import("./components/CricketGlossary");
const loadCricketCalculators = () => import("./components/CricketCalculators");
const loadAuthPages = () => import("./components/AuthPages");
const loadDeleteAccountInfoPage = () =>
  import("./components/DeleteAccountInfoPage");
const loadAccountSettingsPage = () =>
  import("./components/AccountSettingsPage");
const loadAnalyticsDashboardPage = () =>
  import("./components/AnalyticsDashboardPage");
const loadPromoBannerAdminPage = () =>
  import("./components/PromoBannerAdminPage");

const Home = lazy(loadHome);
const CricketScorer = lazy(loadCricketScorer);
const JoinGame = lazy(loadJoinGame);
const ViewCricketScorer = lazy(loadViewCricketScorer);
const ViewSavedMatch = lazy(loadViewSavedMatch);
const PrivacyPolicy = lazy(loadPrivacyPolicy);
const Disclaimer = lazy(loadDisclaimer);
const HowItWorks = lazy(loadHowItWorks);
const About = lazy(loadAbout);
const DownloadAppPage = lazy(loadDownloadAppPage);
const AppPreferencesPage = lazy(loadAppPreferencesPage);
const MatchHistoryPage = lazy(loadMatchHistoryPage);
const TournamentManager = lazy(loadTournamentManager);
const TournamentDetail = lazy(loadTournamentDetail);
const TeamLibraryManager = lazy(loadTeamLibraryManager);
const SiteMapPage = lazy(loadSiteMapPage);
const SupportPage = lazy(loadSupportPage);
const CricketScoringGuide = lazy(loadCricketScoringGuide);
const FaqPage = lazy(loadFaqPage);
const ContactPage = lazy(loadContactPage);
const TermsOfUse = lazy(loadTermsOfUse);
const ScorekeepingTips = lazy(loadScorekeepingTips);
const CricketRulesGuide = lazy(loadCricketRulesGuide);
const CricketMatchFormats = lazy(loadCricketMatchFormats);
const CricketStatisticsGuide = lazy(loadCricketStatisticsGuide);
const CricketResources = lazy(loadCricketResources);
const CricketTournamentGuide = lazy(loadCricketTournamentGuide);
const NotFound = lazy(loadNotFound);
const LearnHub = lazy(loadLearnHub);
const ArticlePage = lazy(loadArticlePage);
const CricketGlossary = lazy(loadCricketGlossary);
const CricketCalculatorsPage = lazy(loadCricketCalculators);
const LoginPage = lazy(() =>
  loadAuthPages().then((module) => ({ default: module.LoginPage })),
);
const SignupPage = lazy(() =>
  loadAuthPages().then((module) => ({ default: module.SignupPage })),
);
const ResetPasswordPage = lazy(() =>
  loadAuthPages().then((module) => ({ default: module.ResetPasswordPage })),
);
const AccountSettingsPage = lazy(loadAccountSettingsPage);
const DeleteAccountInfoPage = lazy(loadDeleteAccountInfoPage);
const AnalyticsDashboardPage = lazy(loadAnalyticsDashboardPage);
const PromoBannerAdminPage = lazy(loadPromoBannerAdminPage);

const routePreloaders = [
  loadHome,
  loadCricketScorer,
  loadJoinGame,
  loadViewCricketScorer,
  loadViewSavedMatch,
  loadPrivacyPolicy,
  loadDisclaimer,
  loadHowItWorks,
  loadAbout,
  loadDownloadAppPage,
  loadAppPreferencesPage,
  loadMatchHistoryPage,
  loadTournamentManager,
  loadTournamentDetail,
  loadTeamLibraryManager,
  loadSiteMapPage,
  loadSupportPage,
  loadCricketScoringGuide,
  loadFaqPage,
  loadContactPage,
  loadTermsOfUse,
  loadScorekeepingTips,
  loadCricketRulesGuide,
  loadCricketMatchFormats,
  loadCricketStatisticsGuide,
  loadCricketResources,
  loadCricketTournamentGuide,
  loadNotFound,
  loadLearnHub,
  loadArticlePage,
  loadCricketGlossary,
  loadCricketCalculators,
  loadAuthPages,
  loadAccountSettingsPage,
  loadDeleteAccountInfoPage,
  loadPromoBannerAdminPage,
];

export const preloadRouteModule = (pathname: string): Promise<unknown> => {
  if (pathname === "/") return loadHome();
  if (pathname === "/create-game") return loadCricketScorer();
  if (pathname === "/join-game") return loadJoinGame();
  if (pathname.startsWith("/join-game/")) return loadViewCricketScorer();
  if (pathname === "/match-history") return loadMatchHistoryPage();
  if (pathname.startsWith("/match-history/")) return loadViewSavedMatch();
  if (pathname === "/privacy-policy") return loadPrivacyPolicy();
  if (pathname === "/disclaimer") return loadDisclaimer();
  if (pathname === "/how-it-works") return loadHowItWorks();
  if (pathname === "/about") return loadAbout();
  if (pathname === "/download-app") return loadDownloadAppPage();
  if (pathname === "/app-preferences") return loadAppPreferencesPage();
  if (pathname === "/tournaments") return loadTournamentManager();
  if (pathname.startsWith("/tournaments/")) return loadTournamentDetail();
  if (pathname === "/my-teams") return loadTeamLibraryManager();
  if (pathname === "/site-map") return loadSiteMapPage();
  if (pathname === "/support") return loadSupportPage();
  if (pathname === "/cricket-scoring-guide") return loadCricketScoringGuide();
  if (pathname === "/faq") return loadFaqPage();
  if (pathname === "/contact") return loadContactPage();
  if (pathname === "/terms") return loadTermsOfUse();
  if (pathname === "/scorekeeping-tips") return loadScorekeepingTips();
  if (pathname === "/cricket-rules-guide") return loadCricketRulesGuide();
  if (pathname === "/cricket-match-formats") return loadCricketMatchFormats();
  if (pathname === "/cricket-statistics-guide")
    return loadCricketStatisticsGuide();
  if (pathname === "/cricket-resources") return loadCricketResources();
  if (pathname === "/cricket-tournament-guide")
    return loadCricketTournamentGuide();
  if (pathname === "/learn") return loadLearnHub();
  if (pathname.startsWith("/learn/")) return loadArticlePage();
  if (pathname === "/cricket-glossary") return loadCricketGlossary();
  if (
    pathname === "/cricket-calculators" ||
    pathname.startsWith("/cricket-calculators/")
  )
    return loadCricketCalculators();
  if (
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/reset-password"
  ) {
    return loadAuthPages();
  }
  if (pathname === "/account") return loadAccountSettingsPage();
  if (pathname === "/delete-account") return loadDeleteAccountInfoPage();
  if (pathname === "/admin/analytics") return loadAnalyticsDashboardPage();
  if (pathname === "/admin/promo-banner") return loadPromoBannerAdminPage();
  return loadNotFound();
};

const RouteLoadingFallback = () => {
  const shouldSuppressInitialFallback =
    typeof window !== "undefined" &&
    window.__APP_SUPPRESS_INITIAL_ROUTE_FALLBACK__ === true;

  if (shouldSuppressInitialFallback) {
    return null;
  }

  return <AppLoader variant="page" />;
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize Google Analytics page view tracking hook
  useGAPageTracking();
  // Initialize Google Analytics click tracking hook
  useGAClickTracking();
  // Initialize our own self-hosted daily-tracking page view hook
  useAppAnalyticsTracking();

  if (Capacitor.getPlatform() === "ios") {
    document.documentElement.classList.add("ios");
  }

  const isPrerenderUserAgent =
    typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";
  React.useEffect(() => {
    applyAppPreferences(getStoredAppPreferences());
  }, []);
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.__APP_SUPPRESS_INITIAL_ROUTE_FALLBACK__ = false;
    }
  }, []);
  // Loads Google's AdSense script only after this component has actually
  // mounted -- i.e. only after the initial hydration pass has already
  // committed successfully. AdSense's script actively scans the DOM once
  // it loads (that's how "Auto ads" / page-level ads work) and can mutate
  // ins.adsbygoogle nodes -- including the one AdSenseBanner already
  // renders into the prerendered HTML -- for a real user, independently of
  // and racing against React's own hydration. Loading it from a
  // synchronous <head> script (as this used to do, in public/index.html)
  // gave it every chance to win that race on a real visit, which is what
  // was producing hydration errors #418/#423 in production regardless of
  // login state -- every page load hit it, not just AuthService-dependent
  // ones. A useEffect can't run until after commit, so this ordering is
  // guaranteed rather than a matter of network timing.
  React.useEffect(() => {
    if (isPrerenderUserAgent || typeof document === "undefined") {
      return;
    }
    // Native app: ads come from AdMob, never AdSense.
    if (Capacitor.isNativePlatform()) {
      return;
    }
    if (document.getElementById("adsbygoogle-loader")) {
      return;
    }
    const adsenseScript = document.createElement("script");
    adsenseScript.id = "adsbygoogle-loader";
    adsenseScript.async = true;
    adsenseScript.src =
      "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6031242056409187";
    document.head.appendChild(adsenseScript);
  }, [isPrerenderUserAgent]);
  React.useEffect(() => {
    const preventDefault = (event: Event) => {
      event.preventDefault();
    };

    document.addEventListener("copy", preventDefault);
    document.addEventListener("cut", preventDefault);
    document.addEventListener("selectstart", preventDefault);
    document.addEventListener("dblclick", preventDefault, { passive: false });

    return () => {
      document.removeEventListener("copy", preventDefault);
      document.removeEventListener("cut", preventDefault);
      document.removeEventListener("selectstart", preventDefault);
      document.removeEventListener("dblclick", preventDefault);
    };
  }, []);
  React.useEffect(() => {
    if (isPrerenderUserAgent) {
      return undefined;
    }

    let cancelled = false;
    const browserWindow = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (
          callback: IdleRequestCallback,
          options?: IdleRequestOptions,
        ) => number;
        cancelIdleCallback?: (handle: number) => void;
      };
    const preloadRoutes = () => {
      if (cancelled) return;
      routePreloaders.forEach((loadRoute) => {
        void loadRoute();
      });
    };

    if (typeof window === "undefined") {
      return undefined;
    }

    if (typeof browserWindow.requestIdleCallback === "function") {
      const idleId = browserWindow.requestIdleCallback(preloadRoutes, {
        timeout: 1500,
      });
      return () => {
        cancelled = true;
        browserWindow.cancelIdleCallback?.(idleId);
      };
    }

    const timeoutId = window.setTimeout(preloadRoutes, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isPrerenderUserAgent]);

  const { pathname } = location;
  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return undefined;
    }

    let listener:
      | {
          remove: () => Promise<void>;
        }
      | undefined;
    let isActive = true;

    void CapacitorApp.addListener("backButton", () => {
      const currentPath = window.location.pathname || "/";

      if (currentPath === "/") {
        void CapacitorApp.exitApp();
        return;
      }

      // A match being scored: let the scorer screen confirm first (it
      // cancels this event and shows its "leave game?" dialog).
      if (currentPath.startsWith("/create-game")) {
        const leaveRequest = new Event("app:request-leave-game", {
          cancelable: true,
        });
        if (!window.dispatchEvent(leaveRequest)) {
          return;
        }
      }

      if (window.history.length > 1) {
        navigate(-1);
        return;
      }

      navigate("/", { replace: true });
    }).then((handle) => {
      if (!isActive) {
        void handle.remove();
        return;
      }
      listener = handle;
    });

    return () => {
      isActive = false;
      void listener?.remove();
    };
  }, [navigate]);

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const timer = window.setTimeout(() => {
      SplashScreen.hide();
    }, 300);

    return () => window.clearTimeout(timer);
  }, []);

  const hideFooter =
    pathname.startsWith("/create-game") || pathname.startsWith("/join-game");
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          background:
            "var(--app-page-gradient, linear-gradient(135deg, #43cea2 0%, #185a9d 100%))",
          position: "relative",
          // Exactly one screen tall (minus the iOS status-bar padding on
          // #root), so short screens never get a stray scrollbar.
          minHeight: "calc(100dvh - var(--app-root-offset, 0px))",
          overflowX: "hidden",
          width: "100%",
        }}
      >
        <AdBannerController />
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <FirstVisitModal />
          <DeepLinkHandler />
          <Box
            component="main"
            sx={{
              flex: 1,
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SpeedInsights />
            <ScrollToTop />
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/create-game" element={<CricketScorer />} />
                <Route path="/join-game" element={<JoinGame />} />
                <Route path="/match-history" element={<MatchHistoryPage />} />
                <Route
                  path="/join-game/:gameId"
                  element={<ViewCricketScorer />}
                />
                <Route
                  path="/match-history/:historyId"
                  element={<ViewSavedMatch />}
                />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/about" element={<About />} />
                <Route path="/get-app" element={<GetAppRedirect />} />
                <Route
                  path="/download-app"
                  element={
                    IS_IOS_APP ? <Navigate to="/" replace /> : <DownloadAppPage />
                  }
                />
                <Route
                  path="/app-preferences"
                  element={<AppPreferencesPage />}
                />
                <Route path="/tournaments" element={<TournamentManager />} />
                <Route
                  path="/tournaments/:tournamentId"
                  element={<TournamentDetail />}
                />
                <Route path="/my-teams" element={<TeamLibraryManager />} />
                <Route path="/site-map" element={<SiteMapPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route
                  path="/cricket-scoring-guide"
                  element={<CricketScoringGuide />}
                />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/terms" element={<TermsOfUse />} />
                <Route
                  path="/scorekeeping-tips"
                  element={<ScorekeepingTips />}
                />
                <Route
                  path="/cricket-rules-guide"
                  element={<CricketRulesGuide />}
                />
                <Route
                  path="/cricket-match-formats"
                  element={<CricketMatchFormats />}
                />
                <Route
                  path="/cricket-statistics-guide"
                  element={<CricketStatisticsGuide />}
                />
                <Route
                  path="/cricket-resources"
                  element={<CricketResources />}
                />
                <Route
                  path="/cricket-tournament-guide"
                  element={<CricketTournamentGuide />}
                />
                <Route path="/learn" element={<LearnHub />} />
                <Route path="/learn/:slug" element={<ArticlePage />} />
                <Route path="/cricket-glossary" element={<CricketGlossary />} />
                <Route
                  path="/cricket-calculators"
                  element={<CricketCalculatorsPage />}
                />
                <Route
                  path="/cricket-calculators/:slug"
                  element={<CricketCalculatorsPage />}
                />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/account" element={<AccountSettingsPage />} />
                <Route
                  path="/delete-account"
                  element={<DeleteAccountInfoPage />}
                />
                <Route
                  path="/admin/analytics"
                  element={<AnalyticsDashboardPage />}
                />
                <Route
                  path="/admin/promo-banner"
                  element={<PromoBannerAdminPage />}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Box>
          {/* Footer for compliance */}
          {!hideFooter && <Footer />}
        </ThemeProvider>
      </Box>
    </>
  );
};

const AppWithRouter = () => (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

export default AppWithRouter;
